import { useEffect } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { t } from "i18next"
import { Banner } from "@/components/ui/banner"
import { RadioGroup } from "@/components/ui/radio-group"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { RadioGroupItemCustom } from "@/modules/patients/agenda/teleconsultation/booking/components/radio-group-item"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { ADMIN_SERVICES } from "@/modules/patients/agenda/teleconsultation/booking/urgency/urgency-section"
import { Urgency } from "@/types/__generated__/union-types"

type MedicalUrgencyItem = {
  label: string
  value: Urgency
}

const medicalUrgencies: MedicalUrgencyItem[] = [
  {
    label: t("scheduling.waitingRoom.priority.NORMAL"),
    value: "NORMAL",
  },
  {
    label: t("scheduling.waitingRoom.priority.urgent"),
    value: "MEDICAL_URGENT",
  },
  {
    label: t("scheduling.waitingRoom.priority.acute"),
    value: "MEDICAL_ACUTE",
  },
]

interface MedicalUrgencyGroupProps {
  onUrgencyChange?: (urgency: Urgency) => void
  isAutoSwitchedToKidsLine?: boolean
}

const MedicalUrgencyGroup = ({ onUrgencyChange, isAutoSwitchedToKidsLine }: MedicalUrgencyGroupProps) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<TeleconsultationForm>()
  const serviceWatch = useWatch({ control, name: "service" })
  const urgencyWatch = useWatch({ control, name: "urgency" })

  const isAdminService = ADMIN_SERVICES.includes(serviceWatch)

  const isEmergencyService = getServiceMapping().serviceMappings.find(
    (mapping) => mapping.service === serviceWatch,
  )?.isUrgent

  useEffect(() => {
    if (isEmergencyService && urgencyWatch === "NORMAL") {
      setValue("urgency", "MEDICAL_URGENT")
    }
  }, [isEmergencyService, setValue, urgencyWatch])

  useEffect(() => {
    if (isAdminService) {
      setValue("urgency", "NORMAL")
    }
  }, [isAdminService, setValue])

  if (isAdminService) return null

  return (
    <div className="flex flex-col gap-2" data-testid="medical-urgency">
      <label className="text-on-surface-subdued text-sm" data-testid="medical-urgency-label">
        {t("scheduling.waitingRoom.priority.medicalUrgency")}
      </label>
      <Controller
        control={control}
        name="urgency"
        render={({ field: { value, onChange } }) => (
          <RadioGroup
            value={value}
            defaultValue="NORMAL"
            className="flex flex-wrap gap-2"
            data-testid="medical-urgency-group"
            onValueChange={(e: Urgency) => {
              onChange(e)
              onUrgencyChange?.(e)
            }}>
            {medicalUrgencies.map((urgency) => {
              const { value, label } = urgency
              return (
                <RadioGroupItemCustom
                  key={value}
                  value={value}
                  groupName="medical-urgency"
                  label={label}
                  disabled={isEmergencyService && value === "NORMAL"}
                />
              )
            })}
          </RadioGroup>
        )}
      />
      {isAutoSwitchedToKidsLine && (
        <Banner type="inline" variant="info" data-testid="kids-line-auto-set-urgency-hint">
          {t("patient.agenda.urgency.inform.kidsLineAutoSet")}
        </Banner>
      )}
    </div>
  )
}

export { MedicalUrgencyGroup }
